/**
 * PricingWorkbench (#3034, T13b1): the management screen of part pricing. Category margin rules and the category
 * recalculation, per-part pricing mode, customer prices and campaigns, all on the server's resolver and rules.
 *
 * Not mounted anywhere yet: the inventory page picks it up in a later task. Read access is `inventory.read`;
 * every control that changes something needs `inventory.pricing.manage`, and the cost-bearing recalculation also
 * `reports.view_financial`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Button, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { usePricingCacheGuard, usePricingPermissions } from '@/hooks/usePartPricing';
import { CampaignsPanel } from './CampaignsPanel';
import { CategoryRecalculation } from './CategoryRecalculation';
import { CategoryRulesPanel } from './CategoryRulesPanel';
import { CustomerPricesPanel } from './CustomerPricesPanel';
import { OwnedBoundary } from './OwnedBoundary';
import { PartPricingPanel } from './PartPricingPanel';
import { useJournalView, type JournalKind } from './pricingJournal';
import { usePricingAccess } from './pricingSession';

type TabId = 'rules' | 'parts' | 'customerPrices' | 'campaigns';
const TABS: readonly TabId[] = ['rules', 'parts', 'customerPrices', 'campaigns'];

const TAB_OF_KIND: Record<JournalKind, TabId> = {
  customer_price_create: 'customerPrices',
  campaign_create: 'campaigns',
  campaign_item_add: 'campaigns',
  recalc_apply: 'rules',
};

/**
 * Requests that were sent and never answered, kept for their owner (also across a reload). They block new saves of the same kind
 * until the owner retries the same request or knowingly discards it, so the way to them is shown here, not only inside a tab that
 * might not have been opened yet.
 */
function PendingRequestsNotice({ onOpen }: { onOpen: (tab: TabId) => void }) {
  const { t } = useTranslation('partPricing');
  const { owner } = usePricingAccess();
  const entries = useJournalView(owner).entries.filter((entry) => entry.persisted);
  if (entries.length === 0) return null;
  const tabs = [...new Set(entries.map((entry) => TAB_OF_KIND[entry.kind as JournalKind]))];
  return (
    <Alert severity="warning" role="alert">
      <AlertTitle>{t('journal.pendingTitle')}</AlertTitle>
      <Stack spacing={1}>
        <span>{t('journal.pendingBody', { count: entries.length })}</span>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {tabs.map((tab) => (
            <Button key={tab} size="small" color="warning" variant="outlined" onClick={() => onOpen(tab)}>
              {t('journal.openTab', { tab: t(`tabs.${tab}`) })}
            </Button>
          ))}
        </Stack>
      </Stack>
    </Alert>
  );
}

/**
 * Everything below belongs to one owner (tenant, user, session family) and the rights it holds. `OwnedBoundary` remounts the
 * subtree in the render that sees an owner or rights change, so drafts, dialogs, selections, previews and names of another
 * owner (or of richer rights) are never shown, not even for one frame.
 */
export function PricingWorkbench() {
  usePricingCacheGuard();
  return (
    <OwnedBoundary>
      <WorkbenchBody />
    </OwnedBoundary>
  );
}

function WorkbenchBody() {
  const { t } = useTranslation('partPricing');
  const { canRead, canManage } = usePricingPermissions();
  const [tab, setTab] = useState<TabId>('rules');
  // A tab that has been opened stays mounted (hidden when another is shown): an unsent draft, a recalculation preview or
  // a request whose outcome is still unknown must not vanish because the user looked at another tab.
  const [visited, setVisited] = useState<ReadonlySet<TabId>>(() => new Set<TabId>(['rules']));

  if (!canRead) {
    return <Alert severity="warning">{t('workbench.noAccess')}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">{t('workbench.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('workbench.intro')}
        </Typography>
      </Box>
      <Alert severity="info">{t('workbench.noAutoReprice')}</Alert>
      {!canManage && <Alert severity="info">{t('workbench.readOnly')}</Alert>}
      <PendingRequestsNotice
        onOpen={(next) => {
          setTab(next);
          setVisited((current) => new Set([...current, next]));
        }}
      />
      <Tabs
        value={tab}
        onChange={(_event, next: TabId) => {
          setTab(next);
          setVisited((current) => new Set([...current, next]));
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label={t('workbench.title')}
      >
        {TABS.map((id) => (
          <Tab key={id} value={id} label={t(`tabs.${id}`)} id={`pricing-tab-${id}`} aria-controls={`pricing-panel-${id}`} />
        ))}
      </Tabs>
      {TABS.filter((id) => visited.has(id)).map((id) => (
        <Box key={id} role="tabpanel" hidden={tab !== id} id={`pricing-panel-${id}`} aria-labelledby={`pricing-tab-${id}`}>
          {id === 'rules' && (
            <Stack spacing={4}>
              <CategoryRulesPanel />
              <Divider />
              <CategoryRecalculation />
            </Stack>
          )}
          {id === 'parts' && <PartPricingPanel />}
          {id === 'customerPrices' && <CustomerPricesPanel />}
          {id === 'campaigns' && <CampaignsPanel />}
        </Box>
      ))}
    </Stack>
  );
}
